/**
 * Lyrics Renderer - Scrolling lyrics overlay for audio visualizer
 * Supports time-synced highlighting when aligned lyrics are available
 */

class LyricsRenderer {
  constructor() {
    this.config = {
      enabled: true,
      fontSize: 48,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#ffffff',
      shadowColor: '#000000',
      shadowBlur: 8,
      lineHeight: 1.4,
      scrollStartY: 1.0,    // 1.0 = bottom of canvas, 0.0 = top
      scrollEndY: 0.0,      // Where text ends scrolling (0.0 = top)
      marginX: 0.1,         // Horizontal margin (% of width)
      // Time-synced highlighting options
      highlightEnabled: true,
      activeColor: '#00ffff',      // Cyan highlight for current line
      dimOpacity: 0.4,             // Opacity for non-active lines
      transitionSmooth: true       // Smooth color transitions (reserved for future use)
    };
  }

  /**
   * Update configuration
   */
  setConfig(newConfig) {
    Object.assign(this.config, newConfig);
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return { ...this.config };
  }

  /**
   * Calculate scroll parameters for a track
   * @param {string} lyrics - The lyrics text
   * @param {number} duration - Track duration in seconds
   * @param {number} canvasWidth - Canvas width in pixels
   * @param {number} canvasHeight - Canvas height in pixels
   * @param {Object|null} alignedLyrics - Optional aligned lyrics with timing data
   * @returns {Object|null} Scroll parameters or null if no lyrics
   */
  calculateScrollParams(lyrics, duration, canvasWidth, canvasHeight, alignedLyrics = null) {
    if (!lyrics || !duration) return null;

    const { fontSize, fontWeight, fontFamily, lineHeight, scrollStartY, scrollEndY } = this.config;

    // Use aligned lyrics if available, otherwise fall back to plain text
    const lines = alignedLyrics?.lines || lyrics.split('\n').map(text => ({ text }));
    const hasTimingData = alignedLyrics?.lines?.length > 0;

    const lineHeightPx = fontSize * lineHeight;
    const textHeight = lines.length * lineHeightPx;

    // Scroll distance = from startY to endY + textHeight (so last line exits)
    const startY = canvasHeight * scrollStartY;
    const endY = canvasHeight * scrollEndY;
    const totalScrollDistance = (startY - endY) + textHeight;

    // Pixels per second
    const scrollSpeed = totalScrollDistance / duration;

    return {
      lines,
      lineHeightPx,
      textHeight,
      startY,
      endY,
      scrollSpeed,
      duration,
      hasTimingData  // Indicates timing data is available for highlighting
    };
  }

  /**
   * Find the active line index based on current time
   * @param {Array} lines - Array of line objects with timing data
   * @param {number} currentTime - Current playback time in seconds
   * @returns {number} Active line index or -1 if none
   */
  findActiveLine(lines, currentTime) {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.start_s !== undefined && line.end_s !== undefined) {
        if (currentTime >= line.start_s && currentTime <= line.end_s) {
          return i;
        }
        // Handle gap: if we're past this line but before the next, still show this one
        if (currentTime > line.end_s && i < lines.length - 1) {
          const nextLine = lines[i + 1];
          if (nextLine.start_s !== undefined && currentTime < nextLine.start_s) {
            return i;
          }
        }
      }
    }
    return -1;
  }

  /**
   * Convert a CSS color string to rgba() with given opacity.
   * Supports hex (#fff, #ffffff, #rrggbbaa) and falls back to rgba(255,255,255,opacity).
   * @param {string} cssColor - CSS color value
   * @param {number} opacity - Opacity 0-1
   * @returns {string} rgba() color string
   */
  _colorWithOpacity(cssColor, opacity) {
    // Handle hex colors
    const hex = cssColor?.match(/^#([0-9a-f]{3,8})$/i)?.[1];
    if (hex) {
      let r, g, b;
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
      }
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // Fallback: white with opacity (same as previous hardcoded behavior)
    return `rgba(255, 255, 255, ${opacity})`;
  }

  /**
   * Render lyrics at given timestamp
   * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
   * @param {HTMLCanvasElement|OffscreenCanvas} canvas - Canvas element
   * @param {Object} scrollParams - Pre-calculated scroll parameters
   * @param {number} currentTime - Current playback time in seconds
   */
  render(ctx, canvas, scrollParams, currentTime) {
    if (!scrollParams || !this.config.enabled) return;

    const { lines, lineHeightPx, startY, scrollSpeed, hasTimingData } = scrollParams;
    const { fontSize, fontFamily, fontWeight, color, shadowColor, shadowBlur, marginX,
            highlightEnabled, activeColor, dimOpacity } = this.config;

    // Calculate current Y offset
    const scrollOffset = currentTime * scrollSpeed;
    const baseY = startY - scrollOffset;

    // Find active line if timing data available
    const activeLineIndex = (hasTimingData && highlightEnabled)
      ? this.findActiveLine(lines, currentTime)
      : -1;

    // Set up text rendering
    ctx.save();
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    const centerX = canvas.width / 2;
    const maxWidth = canvas.width * (1 - marginX * 2);

    // Render each line
    for (let i = 0; i < lines.length; i++) {
      const lineY = baseY + (i * lineHeightPx);

      // Skip lines outside viewport (optimization)
      if (lineY < -lineHeightPx || lineY > canvas.height + lineHeightPx) continue;

      // Get line text - handle both object format (aligned) and string format (plain)
      const lineText = (typeof lines[i] === 'string' ? lines[i] : lines[i].text || '').trim();
      if (!lineText) continue;

      // Apply highlighting based on timing
      if (activeLineIndex !== -1) {
        if (i === activeLineIndex) {
          // Active line - bright highlight
          ctx.fillStyle = activeColor;
          ctx.shadowColor = activeColor;
          ctx.shadowBlur = shadowBlur * 1.5;
        } else {
          // Non-active lines - dimmed using configured color
          ctx.fillStyle = this._colorWithOpacity(color, dimOpacity);
          ctx.shadowColor = shadowColor;
          ctx.shadowBlur = shadowBlur * 0.5;
        }
      } else {
        // No timing data - use default styling
        ctx.fillStyle = color;
        ctx.shadowColor = shadowColor;
        ctx.shadowBlur = shadowBlur;
      }

      ctx.fillText(lineText, centerX, lineY, maxWidth);
    }

    ctx.restore();
  }
}

// ES Module export
export { LyricsRenderer };
